import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { authAPI, settingsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// ── Password strength indicator ───────────────────────────────
function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score  = checks.filter(Boolean).length;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'];
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1,2,3,4].map(i => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= score ? colors[score] : 'bg-gray-200'}`} />
        ))}
      </div>
      <p className={`text-xs font-medium ${['','text-red-500','text-amber-500','text-blue-500','text-green-600'][score]}`}>
        {labels[score]}
      </p>
    </div>
  );
}

// ── Show/hide password field ──────────────────────────────────
function PasswordInput({ label, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="form-label">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          className="form-input pr-10"
          value={value}
          onChange={onChange}
          placeholder={placeholder || '••••••••'}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy-600"
          tabIndex={-1}
        >
          {show
            ? <EyeSlashIcon className="h-4 w-4" />
            : <EyeIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function AdminSettingsPage() {
  const { user } = useAuth();

  // ── Change password state ─────────────────────────────────
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const setPw = (k, v) => setPwForm(f => ({ ...f, [k]: v }));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword)
      return toast.error('New passwords do not match.');
    if (pwForm.newPassword.length < 8)
      return toast.error('New password must be at least 8 characters.');
    if (pwForm.newPassword === pwForm.currentPassword)
      return toast.error('New password must be different from current password.');

    setPwSaving(true);
    try {
      await authAPI.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword:     pwForm.newPassword,
      });
      toast.success('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  // ── Site settings state ───────────────────────────────────
  const [settings, setSettings] = useState({});
  const [stSaving, setStSaving] = useState(false);

  useEffect(() => {
    settingsAPI.getAll()
      .then(r => {
        const rows = Array.isArray(r.data?.data) ? r.data.data : [];
        const map = {};
        rows.forEach(s => {
          if (s && s.setting_key) map[s.setting_key] = s.value || '';
        });
        setSettings(map);
      })
      .catch(() => setSettings({}));
  }, []);

  const setSetting = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setStSaving(true);
    try {
      const arr = Object.entries(settings || {}).map(([setting_key, value]) => ({ setting_key, value: value || '' }));
      await settingsAPI.update({ settings: arr });
      toast.success('Settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setStSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-navy-800">Settings</h1>
        <p className="text-gray-400 text-sm mt-0.5">Manage your account and site configuration</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">

        {/* ── Change Password ─────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl bg-navy-50 flex items-center justify-center text-lg">🔐</div>
            <div>
              <h2 className="font-bold text-navy-700">Change Password</h2>
              <p className="text-gray-400 text-xs">Logged in as <strong>{user?.email}</strong></p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <PasswordInput
              label="Current Password *"
              value={pwForm.currentPassword}
              onChange={e => setPw('currentPassword', e.target.value)}
              placeholder="Your current password"
            />
            <PasswordInput
              label="New Password *"
              value={pwForm.newPassword}
              onChange={e => setPw('newPassword', e.target.value)}
              placeholder="Min. 8 characters"
            />
            {pwForm.newPassword && <PasswordStrength password={pwForm.newPassword} />}
            <PasswordInput
              label="Confirm New Password *"
              value={pwForm.confirmPassword}
              onChange={e => setPw('confirmPassword', e.target.value)}
              placeholder="Repeat new password"
            />

            {/* Match indicator */}
            {pwForm.confirmPassword && (
              <p className={`text-xs font-medium ${
                pwForm.newPassword === pwForm.confirmPassword
                  ? 'text-green-600' : 'text-red-500'
              }`}>
                {pwForm.newPassword === pwForm.confirmPassword
                  ? '✅ Passwords match'
                  : '❌ Passwords do not match'}
              </p>
            )}

            <button
              type="submit"
              disabled={pwSaving}
              className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 mt-2"
            >
              {pwSaving
                ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Changing...</>
                : '🔐 Change Password'}
            </button>
          </form>
        </div>

        {/* ── Site Settings ───────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl bg-cyan-50 flex items-center justify-center text-lg">⚙️</div>
            <div>
              <h2 className="font-bold text-navy-700">Site Settings</h2>
              <p className="text-gray-400 text-xs">General website configuration</p>
            </div>
          </div>

          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label className="form-label">Site Name</label>
              <input className="form-input" value={settings.site_name || ''} onChange={e => setSetting('site_name', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Arabic Name</label>
              <input className="form-input text-right" dir="rtl" style={{ fontFamily: 'Amiri, serif' }} value={settings.site_name_ar || ''} onChange={e => setSetting('site_name_ar', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Contact Email</label>
              <input type="email" className="form-input" value={settings.contact_email || ''} onChange={e => setSetting('contact_email', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Contact Phone</label>
              <input className="form-input" value={settings.contact_phone || ''} onChange={e => setSetting('contact_phone', e.target.value)} />
            </div>
            <div>
              <label className="form-label">WhatsApp Number</label>
              <input className="form-input" value={settings.whatsapp_number || ''} onChange={e => setSetting('whatsapp_number', e.target.value)} placeholder="+234..." />
            </div>
            <div>
              <label className="form-label">Facebook URL</label>
              <input className="form-input" value={settings.facebook_url || ''} onChange={e => setSetting('facebook_url', e.target.value)} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className="form-label">YouTube URL</label>
              <input className="form-input" value={settings.youtube_url || ''} onChange={e => setSetting('youtube_url', e.target.value)} placeholder="https://youtube.com/..." />
            </div>

            <button type="submit" disabled={stSaving} className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 mt-2">
              {stSaving
                ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</>
                : '💾 Save Settings'}
            </button>
          </form>
        </div>

        {/* ── Account Info card ───────────────────────────── */}
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl p-6 text-white lg:col-span-2">
          <h3 className="font-bold mb-4 text-cyan-400 text-sm uppercase tracking-wider">Account Info</h3>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div><p className="text-white/50 mb-0.5">Name</p><p className="font-semibold">{user?.full_name}</p></div>
            <div><p className="text-white/50 mb-0.5">Email</p><p className="font-semibold">{user?.email}</p></div>
            <div>
              <p className="text-white/50 mb-0.5">Role</p>
              <span className="inline-block bg-cyan-400/20 border border-cyan-400/30 text-cyan-300 text-xs font-bold px-2.5 py-1 rounded-full capitalize">
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}
